import type {
  ReactTable,
  Cell,
  CellData,
  Column,
  Header,
  Row,
  RowData,
  TableFeatures,
} from '@tanstack/react-table';
import {
  type PropsWithChildren,
  useContext,
  createContext,
  type JSX,
} from 'react';

import type { TDataTableFeatures } from '../features/types/features.types.js';
import type { DataTableColumnSettingsConfig } from '../public.api.js';

//#region table context
const DataTableContext = createContext<ReactTable<
  TableFeatures,
  RowData
> | null>(null);

/** @internal */
export function DataTableContextProvider<
  TFeatures extends TDataTableFeatures,
  TData extends RowData,
>(props: PropsWithChildren<{ value: ReactTable<TFeatures, TData> }>) {
  return (
    <DataTableContext.Provider value={props.value}>
      {props.children}
    </DataTableContext.Provider>
  );
}

/** @internal */
export function useDataTable(): ReactTable<TDataTableFeatures, RowData> {
  const table = useContext<ReactTable<TDataTableFeatures, RowData> | null>(
    DataTableContext,
  );

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (table === null) {
    throw new Error('Table instance is not defined in context.');
  }

  return table;
}

//#endregion

//#region content context
/** @internal */
interface DataTableContentContextProps {
  hasOverflowLeft: boolean;
  hasOverflowRight: boolean;
  isHorizontallyScrollable: boolean;
  cellActionAriaLabel: string;
}

const DEFAULT_CONTENT_CONTEXT = {
  hasOverflowLeft: false,
  hasOverflowRight: false,
  isHorizontallyScrollable: false,
  cellActionAriaLabel: '',
};

export const DataTableContentContext =
  createContext<DataTableContentContextProps>(DEFAULT_CONTENT_CONTEXT);

/** @internal */
export function DataTableContentContextProvider(
  props: PropsWithChildren<{ value?: DataTableContentContextProps }>,
): JSX.Element {
  return (
    <DataTableContentContext.Provider
      value={props.value ?? DEFAULT_CONTENT_CONTEXT}
    >
      {props.children}
    </DataTableContentContext.Provider>
  );
}

/** @internal */
export function useDataTableContent(): DataTableContentContextProps {
  const content = useContext<DataTableContentContextProps>(
    DataTableContentContext,
  );

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (content === null) {
    throw new Error('Content instance is not defined in context.');
  }

  return content;
}

//#endregion

//#region header context
/** @internal */
export const DataTableHeaderContext = createContext<Header<
  TableFeatures,
  RowData,
  CellData
> | null>(null);

/** @internal */
export function DataTableHeaderContextProvider(
  props: PropsWithChildren<{
    value?: Header<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableHeaderContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableHeaderContext.Provider>
  );
}

/** @internal */
export function useDataTableHeader(): Header<
  TDataTableFeatures,
  RowData,
  CellData
> {
  const header = useContext<Header<
    TDataTableFeatures,
    RowData,
    CellData
  > | null>(DataTableHeaderContext);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (header === null) {
    throw new Error('Header instance is not defined in context.');
  }

  return header;
}

//#endregion

//#region column context
const DataTableColumnContext = createContext<Column<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  unknown
> | null>(null);

/** @internal */
export function DataTableColumnContextProvider(
  props: PropsWithChildren<{
    value?: Column<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableColumnContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableColumnContext.Provider>
  );
}

/** @internal */
export function useDataTableColumn(): Column<
  TDataTableFeatures,
  RowData,
  CellData
> {
  const column = useContext<Column<
    TDataTableFeatures,
    RowData,
    CellData
  > | null>(DataTableColumnContext);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (column === null) {
    throw new Error('Column object is not defined in context.');
  }

  return column;
}

//#endregion

//#region row context
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DataTableRowContext = createContext<Row<any, any> | null>(null);

/** @internal */
export function DataTableRowContextProvider(
  props: PropsWithChildren<{
    value?: Row<TDataTableFeatures, RowData>;
  }>,
): JSX.Element {
  return (
    <DataTableRowContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableRowContext.Provider>
  );
}

/** @internal */
export function useDataTableRow(): Row<TDataTableFeatures, RowData> | null {
  const row = useContext<Row<TDataTableFeatures, RowData> | null>(
    DataTableRowContext,
  );

  return row;
}

//#endregion

//#region cell context
const DataTableCellContext = createContext<Cell<
  TableFeatures,
  RowData,
  unknown
> | null>(null);

/** @internal */
export function DataTableCellContextProvider(
  props: PropsWithChildren<{
    value?: Cell<TDataTableFeatures, RowData, CellData>;
  }>,
): JSX.Element {
  return (
    <DataTableCellContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableCellContext.Provider>
  );
}

/** @internal */
export function useDataTableCell(): Cell<
  TDataTableFeatures,
  RowData,
  CellData
> | null {
  const cell = useContext<Cell<TDataTableFeatures, RowData, CellData> | null>(
    DataTableCellContext,
  );

  return cell;
}

//#endregion

//#region column settings context
/** @internal */
export type DataTableColumnSettingsHandle = {
  openColumnSettings?: (columnSettings?: DataTableColumnSettingsConfig) => void;
};

const DataTableColumnSettingsContext =
  createContext<DataTableColumnSettingsHandle | null>(null);

/** @internal */
export function DataTableColumnSettingsContextProvider(
  props: PropsWithChildren<{
    value?: DataTableColumnSettingsHandle;
  }>,
): JSX.Element {
  return (
    <DataTableColumnSettingsContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableColumnSettingsContext.Provider>
  );
}

/** @internal */
export function useDataTableColumnSettings(): DataTableColumnSettingsHandle | null {
  return useContext<DataTableColumnSettingsHandle | null>(
    DataTableColumnSettingsContext,
  );
}

//#endregion

//#region isHeader context
const DataTableIsHeaderContext = createContext(false);

/** @internal */
export function DataTableIsHeaderContextProvider(
  props: Readonly<PropsWithChildren>,
): JSX.Element {
  return (
    <DataTableIsHeaderContext.Provider value={true}>
      {props.children}
    </DataTableIsHeaderContext.Provider>
  );
}

/** @internal */
export function useDataTableIsHeaderContextProvider(): boolean {
  return useContext<boolean>(DataTableIsHeaderContext);
}
